import { Icon } from '@purpur/library/icon';
import { IconChevronDown } from '@purpur/library/icon/chevron-down';
import AnimateHeight from 'react-animate-height';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';

import { purpurFocus } from '@telialt/common-theme/src/mixins';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

import { BlockQuickLinkColorVariant, BlockQuickLinkDisplayVariant } from './model';

export const QuickLinkCardContainer = styled.div`
    border-radius: ${getPurpurBorder('radiusMd')};
    position: relative;
    text-align: left;
    z-index: 1;
`;

export const QuickLinkCardImageContainer = styled.div`
    display: flex;
    justify-content: center;
    position: relative;
`;

export const QuickLinkCardImage = styled.img`
    max-height: 9.5625rem;
    aspect-ratio: 4/5;
    object-fit: cover;
`;

export const QuickLinkCardContent = styled.div`
    padding: ${getPurpurSpacing('200')};
`;

export const QuickLinkCardContentHead = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${getPurpurSpacing('100')};
    color: ${getPurpurColor('textDefault')};
    line-height: ${getPurpurTypography('lineHeightDefault')};
    > * {
        flex-shrink: 0;
    }
`;

export const QuickLinkCardLinkIconImage = styled.img`
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
    margin-top: -${getPurpurSpacing('25')};
`;

export const QuickLinkCardLinkIcon = styled(Icon)`
    color: ${getPurpurColor('brandOffBlack')};
    margin-top: -${getPurpurSpacing('25')};
`;

export const QuickLinkCardTitle = styled.div`
    flex: 1;
    font-weight: ${getPurpurTypography('weightBold')};
    font-size: ${getPurpurTypography('scale100')};
`;

export const QuickLinkCardDescriptionContainer = styled.div`
    margin-top: ${getPurpurSpacing('100')};
`;

type ExpandedProps = {
    $isForcedCollapsed?: boolean;
};

export const QuickLinkCardChildLinksContainer = styled.ul<ExpandedProps>`
    list-style: none;
    margin: 0;
    padding: ${getPurpurSpacing('200')};
    border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    flex-direction: column;
    gap: ${getPurpurSpacing('100')};
    border-radius: 0 0 ${getPurpurBorder('radiusMd')} ${getPurpurBorder('radiusMd')};
    overflow: hidden;
    display: ${({ $isForcedCollapsed }) => ($isForcedCollapsed ? 'none' : 'flex')};
    height: 100%;

    ${purpurMedia('md')`
        display: flex;
    `};
`;

export const QuickLinkCardChildLink = styled.a`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${getPurpurSpacing('50')};
    color: ${getPurpurColor('textInteractivePrimary')};
    text-decoration: none;
    font-weight: ${getPurpurTypography('weightMedium')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
    padding: ${getPurpurSpacing('50')} 0;

    &:hover {
        color: ${getPurpurColor('textInteractivePrimaryHover')};
    }
    &:active {
        text-decoration: underline;
        color: ${getPurpurColor('textInteractivePrimaryActive')};
    }

    ${purpurFocus()};
`;

type WrapperProps = {
    $colorVariant: BlockQuickLinkColorVariant;
    $displayVariant: BlockQuickLinkDisplayVariant;
    $isExpandable?: boolean;
    $isExpanded?: boolean;
};

export const QuickLinkCardWrapper = styled.div<WrapperProps>`
    display: block;
    border-radius: ${getPurpurBorder('radiusMd')};
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    background: ${({ $colorVariant }) =>
        match($colorVariant)
            .with('primary', () => getPurpurColor('backgroundPrimary'))
            .with('secondary', () => getPurpurColor('backgroundSecondary'))
            .exhaustive()};
    text-decoration: none;
    height: 100%;

    &:active,
    &:hover {
        margin: calc((${getPurpurBorder('widthSm')} - ${getPurpurBorder('widthXs')}) * -1)
            calc((${getPurpurBorder('widthSm')} - ${getPurpurBorder('widthXs')}) * -1);
        border-width: ${getPurpurBorder('widthSm')};
        height: calc(100% + (${getPurpurBorder('widthXs')} * 2));
    }

    &:active {
        border-color: ${getPurpurColor('borderInteractivePrimaryActive')};
        background: ${getPurpurColor('backgroundInteractivePrimaryNegative')};
    }

    &:hover {
        border-color: ${getPurpurColor('borderInteractivePrimaryHover')};
    }

    ${purpurFocus()};

    ${({ $colorVariant, $displayVariant, $isExpandable, $isExpanded }) =>
        $displayVariant === 'card-links' &&
        css`
            display: flex;
            flex-direction: column;
            &:active,
            &:hover {
                margin: 0;
                border-width: ${getPurpurBorder('widthXs')};
                height: 100%;
                border-color: ${getPurpurColor('borderWeak')};
                background: ${match($colorVariant)
                    .with('primary', () => getPurpurColor('backgroundPrimary'))
                    .with('secondary', () => getPurpurColor('backgroundSecondary'))
                    .exhaustive()};
            }

            ${QuickLinkCardContainer} {
                ${$isExpanded &&
                css`
                    border-radius: ${getPurpurBorder('radiusMd')} ${getPurpurBorder('radiusMd')} 0 0;
                `};

                ${$isExpandable &&
                css`
                    cursor: pointer;
                    &:active {
                        margin: calc((${getPurpurBorder('widthSm')}) * -1) calc((${getPurpurBorder('widthSm')}) * -1);
                        border-style: solid;
                        border-width: ${getPurpurBorder('widthSm')};
                        border-color: ${getPurpurColor('borderInteractivePrimaryActive')};
                        background: ${getPurpurColor('backgroundInteractivePrimaryNegative')};
                    }

                    &:hover {
                        margin: calc((${getPurpurBorder('widthSm')}) * -1) calc((${getPurpurBorder('widthSm')}) * -1);
                        border-style: solid;
                        border-width: ${getPurpurBorder('widthSm')};
                        border-color: ${getPurpurColor('borderInteractivePrimaryHover')};
                    }
                    ${purpurFocus()};
                `};

                ${purpurMedia('md')`
                    cursor: default;
                `};
            }
        `};

    ${({ $displayVariant }) =>
        $displayVariant !== 'card-links' &&
        css`
            ${QuickLinkCardContainer} {
                height: 100%;
            }
        `};

    ${QuickLinkCardChildLinksContainer} {
        background: ${({ $colorVariant }) =>
            match($colorVariant)
                .with('primary', () => getPurpurColor('backgroundSecondary'))
                .with('secondary', () => getPurpurColor('backgroundPrimary'))
                .exhaustive()};
    }
`;

type ChevronProps = {
    $isExpanded?: boolean;
};

export const QuickLinkCardExpandChevron = styled(IconChevronDown)<ChevronProps>`
    ${({ $isExpanded }) =>
        $isExpanded &&
        css`
            transform: rotateX(180deg);
        `};
    transition: transform 0.3s ease-in-out;

    ${purpurMedia('md')`
        display: none;
    `};
`;

// Important required to disable collapsing in desktop view
export const StyledAnimateHeight = styled(AnimateHeight)`
    ${purpurMedia('md')`
        flex: 1;
        height: auto !important;
        & > * {
            height: 100%;
            display: block !important;
        }
    `};
`;
